import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/mock";
import { api, formatApiError } from "@/lib/api";

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  zip: "",
  service: "",
  budget: "",
  timeline: "",
  details: "",
};

const BUDGETS = ["< $5,000", "$5,000 – $15,000", "$15,000 – $40,000", "$40,000+", "Not sure yet"];
const TIMELINES = ["ASAP", "1–3 months", "3–6 months", "Just exploring"];

const QuoteForm = () => {
  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (ev) =>
    setData((d) => ({ ...d, [k]: ev.target.value }));

  const validate = () => {
    const e = {};
    if (!data.firstName.trim()) e.firstName = "Required";
    if (!data.lastName.trim()) e.lastName = "Required";
    if (!data.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      e.email = "Invalid email";
    if (!data.phone.trim()) e.phone = "Required";
    if (!data.service) e.service = "Choose a service";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/quote", data);
      setData(initialState);
      toast.success("Quote request received. We'll reach out within 24 hours.");
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
          "Could not submit. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quote-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6"
      noValidate
    >
      <Field label="First Name" error={errors.firstName} testid="quote-firstname">
        <input
          type="text"
          value={data.firstName}
          onChange={onChange("firstName")}
          data-testid="quote-firstname-input"
          className={inputClass(errors.firstName)}
        />
      </Field>
      <Field label="Last Name" error={errors.lastName} testid="quote-lastname">
        <input
          type="text"
          value={data.lastName}
          onChange={onChange("lastName")}
          data-testid="quote-lastname-input"
          className={inputClass(errors.lastName)}
        />
      </Field>

      <Field label="Email" error={errors.email} testid="quote-email">
        <input
          type="email"
          value={data.email}
          onChange={onChange("email")}
          data-testid="quote-email-input"
          className={inputClass(errors.email)}
        />
      </Field>
      <Field label="Phone" error={errors.phone} testid="quote-phone">
        <input
          type="tel"
          value={data.phone}
          onChange={onChange("phone")}
          data-testid="quote-phone-input"
          className={inputClass(errors.phone)}
        />
      </Field>

      <Field label="ZIP Code" testid="quote-zip">
        <input
          type="text"
          value={data.zip}
          onChange={onChange("zip")}
          data-testid="quote-zip-input"
          className={inputClass()}
        />
      </Field>
      <Field label="Service Needed" error={errors.service} testid="quote-service">
        <select
          value={data.service}
          onChange={onChange("service")}
          data-testid="quote-service-input"
          className={`${inputClass(errors.service)} appearance-none cursor-pointer`}
        >
          <option value="">Select one</option>
          {SERVICES.map((s) => (
            <option key={s.slug} value={s.slug}>
              {s.title}
            </option>
          ))}
          <option value="other">Other / Multiple</option>
        </select>
      </Field>

      <Field label="Budget Range" testid="quote-budget">
        <select
          value={data.budget}
          onChange={onChange("budget")}
          data-testid="quote-budget-input"
          className={`${inputClass()} appearance-none cursor-pointer`}
        >
          <option value="">Select one</option>
          {BUDGETS.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Timeline" testid="quote-timeline">
        <select
          value={data.timeline}
          onChange={onChange("timeline")}
          data-testid="quote-timeline-input"
          className={`${inputClass()} appearance-none cursor-pointer`}
        >
          <option value="">Select one</option>
          {TIMELINES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </Field>

      <div className="md:col-span-2">
        <Field label="Project Details" testid="quote-details">
          <textarea
            rows={5}
            value={data.details}
            onChange={onChange("details")}
            data-testid="quote-details-input"
            placeholder="Square footage, materials you've considered, must-haves, etc."
            className={`${inputClass()} resize-none`}
          />
        </Field>
      </div>

      <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-4 mt-4">
        <p className="text-xs text-brand-inkSoft max-w-md">
          By submitting, you agree to be contacted by JMJC about your project.
          We don't share your information.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="quote-submit"
          className="inline-flex items-center gap-3 px-7 py-5 bg-brand-cta hover:bg-brand-ctaHover text-white text-xs font-bold uppercase tracking-[0.16em] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Submitting..." : "Submit Request"}
          <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </form>
  );
};

const inputClass = (hasError) =>
  `w-full bg-transparent border ${
    hasError ? "border-brand-cta" : "border-brand-border"
  } focus:border-brand-cta px-4 py-3 text-base text-brand-ink placeholder:text-brand-inkSoft/50 outline-none transition-colors rounded-none`;

const Field = ({ label, error, children, testid }) => (
  <label data-testid={testid} className="flex flex-col gap-1.5">
    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-inkSoft">
      {label}
    </span>
    {children}
    {error && (
      <span className="text-xs text-brand-cta" data-testid={`${testid}-error`}>
        {error}
      </span>
    )}
  </label>
);

export default QuoteForm;
